<template>
    <div v-if="project" class="article">
        <div v-for="(body, index) in project.data.body.slice(this.start, this.end)" :key="body.slice_type + '-' + index"
            class="body">

            <!-- 🥜 in a Nutshell -->
            <div v-if="body.slice_type == 'in-a-nutshell'">
                <div class="article__nutshell" :class="resolveSection(body.primary)">
                    <div class="container">
                        <!-- <ParagraphRenderer :paragraphs="body.primary.text"></ParagraphRenderer> -->
                        <prismic-rich-text :field="body.primary.text"/>
                    </div>
                </div>
            </div>

            <!-- Only Text 📖 in one big row -->
            <div v-if="body.slice_type == 'text'">
                <div class="article__text" :class="resolveSection(body.primary)">
                    <Scrollama :offset="resolveOffset('text')" :debug="false" :progress="true" @step-enter="stepEnter"
                        @step-exit="stepExit">
                        <div class="container">
                            <div class="grid-x">
                                <div class="columns small-12 medium-12 centered">
                                    <!-- <ParagraphRenderer :paragraphs="body.primary.text"></ParagraphRenderer> -->
                                    <prismic-rich-text :field="body.primary.text" />
                                </div>
                            </div>
                        </div>
                    </Scrollama>
                </div>
            </div>

            <!-- 🏞 fullscreen-image -->
            <div v-if="body.slice_type == 'full-image'">
                <div class="article__full-image" :class="resolveSection(body.primary)">
                    <div class="container">
                        <img loading=lazy :src="body.primary.image.url + '?w=0.1&auto=compress'"
                            :class="{border: body.primary.border}" />
                    </div>
                </div>
            </div>

            <!-- 📖 next to 🏞 image -->
            <div v-if="body.slice_type == 'text-image'">
                <Scrollama :offset="resolveOffset('text-image')" :debug="false" :progress="true" @step-enter="stepEnter"
                    @step-exit="stepExit">
                    <div class="article__text-image" :class="resolveSection(body.primary)">
                        <div class="container text-image">
                            <div class="grid-x" :class="resolveOrder(body)">
                                <div class="columns small-12" :class="resolveLayoutProperties(body, 'first')">
                                    <!-- <ParagraphRenderer :paragraphs="body.primary.text"></ParagraphRenderer> -->

                                    <prismic-rich-text :field="body.primary.text"
                                        :class="resolveAlignment(body.primary.verticalalign)" />
                                </div>
                                <div class="columns small-12" :class="resolveLayoutProperties(body, 'last')">
                                    <img loading=lazy :src="resolveImageQuery(body.primary.image.url)"
                                        :class="{border: body.primary.border}" />
                                </div>
                            </div>
                        </div>
                    </div>
                </Scrollama>
            </div>

            <!-- 2 Columns of 📖 text-text -->
            <div v-if="body.slice_type == 'text-text'">
                <Scrollama :offset="resolveOffset('text-text')" :debug="false" :progress="true" @step-enter="stepEnter"
                    @step-exit="stepExit">
                    <div class="article__text-text" :class="resolveSection(body.primary)">
                        <div class="container text-text">
                            <div class="grid-x">
                                <div class="columns small-12 medium-6"
                                    :style="'padding-top: ' + body.primary.leftoffset + 'rem;'">
                                    <prismic-rich-text :field="body.primary['left-text']" />
                                </div>
                                <div class="columns small-12 medium-6"
                                    :style="'padding-top: ' + body.primary.rightoffset + 'rem;'">
                                    <prismic-rich-text :field="body.primary['right-text']"
                                        :class="{ lowered: isLowered(body.primary['left-text']) }" />
                                </div>
                            </div>
                        </div>
                    </div>
                </Scrollama>
            </div>

            <!-- 1 Columns of 🏞 image-image -->
            <div v-if="body.slice_type == 'image-image'" class="article__image-image"
                :class="resolveSection(body.primary)">
                <div class="container">
                    <div class="grid-x">
                        <div class="columns small-12 medium-6">
                            <img loading=lazy :src="resolveImageQuery(body.primary['left-image'].url)"
                                :class="{border: body.primary.border}">
                        </div>
                        <div class="columns small-12 medium-6">
                            <img loading=lazy :src="resolveImageQuery(body.primary['right-image'].url)"
                                :class="{border: body.primary.border}">
                        </div>
                    </div>
                </div>
            </div>

            <!-- lets 2 Images 🏞 <-> 🏞 switch with opacity fade during scroll -->
            <div v-if="body.slice_type == 'image-switcher'" class="article__image-switcher"
                :class="resolveSection(body.primary)">
                <Scrollama :offset="resolveOffset('image-switcher')" :debug="false" :progress="true"
                    @step-enter="stepEnter" @step-exit="stepExit">
                    <div class="container">
                        <image-switcher :element="body" :topImage="body.primary.topimage.url"
                            :bottomImage="body.primary.bottomimage.url" :text="body.primary.text"></image-switcher>
                    </div>
                </Scrollama>
            </div>

            <!-- let an image 🏞 stick to a border -->
            <div v-if="body.slice_type == 'image-sticky-edge'" class="article__image-sticky-edge">
                <div class="container">
                    <image-sticky :element="body" :image="body.primary.image.url" :edge="body.primary.edge">
                    </image-sticky>
                </div>
            </div>

            <!-- Just a Video embed -->
            <div v-if="body.slice_type == 'text-video'" class="article__video" :class="resolveSection(body.primary)">
                <div class="container">
                    <div class="video">
                        <iframe :src="body.primary.video" width="100%" height="100%" frameborder="0"
                            allow="autoplay; fullscreen" allowfullscreen></iframe>
                    </div>
                </div>
            </div>

            <!-- 🏄 tryout zone -->
            <div v-if="body.slice_type == 'tryout-zone'">
                <a :href="body.primary.link1.url" target="_blank"
                    :style="`--primaryColor: ${project.data.primarycolor}; --secondaryColor: ${project.data.primarycolor}`">
                    <div class="article__tryout-zone" :class="resolveSection(body.primary)">
                        <div class="container">
                            <img loading=lazy :src="body.primary.image.url + '?w=0.1&auto=compress'"
                                :class="{border: body.primary.border}" />
                            <span class="tryout-button">
                                {{ body.primary['button-text'] }}
                            </span>
                        </div>
                    </div>
                </a>
            </div>

            <div v-if="body.slice_type == 'image-slider'">
                <ImageSlider>
                    <div v-for="item in body.items" :key="item.url" style="width: 100%; height: 100%; flex-shrink:0; max-width: 82rem; max-height: 60vh; display: flex;">
                        <img :src="item.image.url" style="width: 100%; object-fit: contain; max-width: 82rem; max-height: 60vh;"/>
                    </div>

                <!-- Add more slides -->
                </ImageSlider>
            </div>

        </div>
    </div>
</template>

<script>
    import ParagraphRenderer from '@/components/modules/ParagraphRenderer.vue'
    import Scrollama from 'vue-scrollama'

    import ImageSwitcher from './modules/sections/ImageSwitcher.vue'
    import ImageSticky from './modules/sections/ImageSticky.vue'
    import ImageSlider from './ImageSlider.vue'

    export default {
        props: {
            project: {
                required: true,
                validate: prop => typeof prop === 'object' || prop === null
            },
            start: {
                required: false
            },
            end: {
                required: false
            }
        },
        components: {
            Scrollama,
            ParagraphRenderer,
            ImageSwitcher,
            ImageSticky,
            ImageSlider
        },
        watch: {
            project: {
                handler: function () {},
                immediate: true
            },
            start: {
                handler: function () {},
                immediate: true
            },
            end: {
                handler: 'resolveEnd',
                immediate: true
            }
        },
        methods: {
            /* Check if the given URL contains a gif and therefore fit the correct compression query*/ 
            resolveImageQuery(url) {
                if(url.includes('.gif')) {
                    return url.replace('?auto=compress,format', '')
                } else {
                    return url
                }
            },
            isLowered(paragraph) {
                for (const element of paragraph) {
                    if (element.type == 'heading1') {
                        return true
                    }
                }
                return false
            },
            // Scrollama functions
            stepEnter({
                element,
                index,
                direction
            }) {
                this.handleUnderline(element, 'enable');
            },
            stepExit({
                element,
                index,
                direction
            }) {
                this.handleUnderline(element, 'disable')
            },
            handleUnderline(element, status) {
                // Enable all custom Underlined Classes
                let underlinedElements = element.querySelectorAll('.underlined')
                underlinedElements.forEach((element, index) => {

                    // Defines the correct class based on word length wich loads different
                    // Underline textures
                    let textLength = element.textContent.length;

                    if (textLength <= 4) {
                        element.classList.add('underline-4')
                    }

                    if (textLength > 4 && textLength <= 6) {
                        element.classList.add('underline-6')
                    }

                    if (textLength > 6 && textLength <= 7) {
                        element.classList.add('underline-7')
                    }

                    if (textLength > 7 && textLength <= 9) {
                        element.classList.add('underline-9')
                    }

                    if (textLength > 9 && textLength <= 11) {
                        element.classList.add('underline-11')
                    }

                    if (textLength > 11 && textLength <= 15) {
                        element.classList.add('underline-15')
                    }

                    if (textLength > 15) {
                        element.classList.add('underline-15')
                    }

                    if (status == 'enable') {
                        element.classList.add('animated-' + index)
                    }
                    if (status == 'disable') {
                        element.classList.remove('animated-' + index)
                    }

                });
            },
            resolveEnd(value) {
                if (value == 'end') {
                    this.end = infinity
                }
            },
            // Checks if the element got some information from the backend about its styling
            // Therefore there are 4 possible values »none, dark, light, draft-stripes«
            // This names are representations for the class names used in css to manipulate the style
            resolveSection(element) {
                let cssClass
                if (element.style != 'none') {
                    cssClass = "article__" + element.style
                }
                // console.log(element.issection)
                // if(element.issection) {
                //     cssClass = "section " + cssClass
                // }
                if (element.superwide) {
                    cssClass = "superwide " + cssClass
                }
                if (element.padding) {
                    cssClass = "p-" + element.padding + " " + cssClass
                }

                // console.log(cssClass)
                return cssClass
            },
            resolveAlignment(alignment) {
                if (alignment === 'top') {
                    return 'alignment-top'
                }
                if (alignment === 'center') {
                    return 'alignment-center'
                }
                if (alignment === 'bottom') {
                    return 'alignment-bottom'
                }
            },
            // Resolves the Layout based on the layout properties from Prismic
            // This are the layouts: 1-1, 1-2, 1-3, 3-1, 2-1
            // The Layout notation defines the sizing relation and the order of the columns
            resolveLayoutProperties(element, order) {
                let layout = element.primary.layout
                let first = layout.charAt(0)
                let last = layout.charAt(2)

                let div = 12 / (parseInt(first) + parseInt(last))


                if (order == 'first') {
                    return "medium-" + first * div
                }

                if (order == 'last') {
                    return "medium-" + last * div
                }

            },
            // Resolves the values of the offset based on the device width
            resolveOffset(type) {
                let width = window.innerWidth;
                let value;

                if (type === 'text') {
                    width < 881 ? value = 0 : 0.2
                }

                if (type === 'text-image') {
                    width < 881 ? value = 0 : 0.2

                }

                if (type === 'text-text') {
                    width < 881 ? value = 0 : 0.5
                }

                if (type === 'image-switcher') {
                    width < 881 ? value = 0 : 0
                }

                return value
            },
            resolveOrder(element) {
                let layout = element.primary.layout
                let first = layout.charAt(0)
                let last = layout.charAt(2)
                // Puts the reverser class on to call flex-direction: row-reverse
                if (first > last) {
                    return 'reverse'
                }
            }
        }
    }
</script>

<style lang="scss">
    @import '../assets/scss/base.scss';

    .article__draft-stripes {
        background-image: $draft-background;
        background-size: 10vw 200.00px;
    }

    .article__light {
        background: $light-background;

        h1,
        h2,
        h3,
        p,
        span {
            color: black !important;
        }

        .underlined::after {
            filter: invert(1)
        }

    }

    .article__dark {
        background: $dark-background;
    }
</style>

<style lang="scss" scoped>
    @import '../assets/scss/base.scss';
    @import '../assets/scss/foundation.scss';

    .section {
        padding: 8rem 1rem;
    }

    .p-0 {
        padding: 0;
    }

    .p-1-1 {
        padding: 1rem 0;
    }

    .p-2-2 {
        padding: 2rem 0;
    }

    .p-4-4 {
        padding: 4rem 0;
    }

    .p-8-8 {
        padding: 8rem 0;
    }



    .article {
        padding-bottom: 3rem;
        .container {
            @include sm () {
                padding-left: 1rem;
                padding-right: 1rem;
            }

            &.text-text,
            &.text-image {
                max-width: 991px;
            }
            
        }

        .alignment-top {
            position: relative;
            top: 0;
        }

        .alignment-center {
            position: relative;
            top: 50%;
            transform: translateY(-50%);
        }

        .alignment-bottom {
            position: relative;
            top: 100%;
            transform: translateY(-100%)
        }

        .border {
            border: .5px solid $white-10;
            border-radius: 4px;
            box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.15);
        }

        .superwide {
            .container {
                margin: 0 auto;
                max-width: 1920px;
            }

            div {
                text-align: center;
            }

            img {
                width: 100% !important;
                max-width: none !important;
                max-height: none !important;
            }
        }

        &__video {
            .video {
                position: relative;
                padding-bottom: 56.857%;
                /* Default for 1600x900 videos 16:9 ratio*/
                padding-top: 0px;
                height: 0;
                overflow: hidden;
                background: $white-100;
                max-width: calc(100vw - 2rem);
                .player {
                    width: auto !important;
                }
            }

            iframe {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                border: 6px solid white;
                border-radius: 6px;
            }
        }

        &__nutshell {
            max-width: 55ch;
            margin: 0 auto;

            &>.container {
                padding: .5rem 2rem;
                background: $white-40;
                border-radius: 12px;
            }
        }

        &__text-text {
            .grid-x.reverse {
                flex-direction: row-reverse;
            }

            .lowered {
                margin-top: 5.68rem;

                @include sm () {
                    margin-top: 0;
                }
            }
        }

        &__text {
            .centered {
                max-width: 55ch;
                margin: 0 auto;
            }

            iframe {
                width: 100%;
                height: 17rem;
            }
        }

        &__text-image {
            img {
                width: 95%;
                margin-left: 5%;
            }

            @include md() {
                img {
                    width: 100%;
                    margin: 0;
                }

                .grid-x {
                    flex-direction: column-reverse;
                }
            }
        }

        &__text-image,
        &__image-image,
        &__full-image &__tryout-zone {
            img {
                height: 100% !important;
                max-height: 80rem;
                object-fit: cover;
            }
        }

        &__tryout-zone {
            padding: 4rem 0;
            position: relative;
            cursor: pointer;

            img {
                border: .5px solid $white-10;
                border-radius: 19px;
                box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.15);
            }

            &:hover {
                .tryout-button {
                    color: #fff;
                    background: linear-gradient(to left, var(--secondaryColor), var(--primaryColor));
                    transition: $t4 $out-quart;
                    box-shadow: 0px 2px 8px 2px rgba(0, 0, 0, 0.09);
                }
            }

            .tryout-button {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translateX(-50%) translateY(-50%);
                z-index: 10;
                padding: 1rem 1.2rem;
                transition: $t4 $out-quart;
                border-radius: 12px;
                color: $dark;
                background: rgba(255, 255, 255, 0.95);
                box-shadow: 0px 2px 8px 2px rgba(0, 0, 0, 0.09);
                // border-image-source: linear-gradient(to left, var(--secondaryColor), var(--primaryColor));
            }
        }

        &__image-image {
            .grid-x>.columns {
                &:first-child {
                    padding-right: .5rem;

                    @include sm () {
                        padding: 0 .5rem;
                    }
                }

                &:last-child {
                    padding-left: .5rem;

                    @include sm () {
                        padding: 0 .5rem;
                    }
                }
            }
        }
    }
</style>